import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useMutation, useQueryClient } from '@tanstack/react-query';

import { deleteExternalInvoices } from 'api/Billing';

import { showAlert } from 'components/shared/Alert/Alert';
import Confirm from 'components/shared/Modal/Confirm/Confirm';
import Popover from 'components/shared/Popovers/Popover/Popover';

import { onSendEmail, onSendText } from '../lib/utils';

import { CustomSendModal } from './contactButton/CustomSendModal';

const SendInvoiceMessagePopUp = ({ data }) => {
  const { id, patient } = data || {};

  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [customModalVisible, setCustomModalVisible] = useState(false);
  const [customContactType, setCustomContactType] = useState('email');

  const { mutate: deleteInvoice, isLoading: isDeleteInvoicesLoading } = useMutation({
    mutationFn: (data) => deleteExternalInvoices(navigate, data)
  });

  const onDeleteInvoice = () => {
    deleteInvoice(
      { id },
      {
        onSuccess: (data) => {
          if (data?.deletedInvoice?.deleted) {
            queryClient.invalidateQueries(['external_invoices']);
            showAlert({
              title: 'Success',
              message: 'Invoices have been deleted successfully',
              color: 'success'
            });
          }
        },
        onError: () => {
          showAlert({
            title: 'Error!',
            message: 'There was an error deleting invoice!',
            color: 'danger'
          });
        }
      }
    );
  };

  const onOpenDeleteDialog = () => {
    setShowConfirmationModal(true);
  };

  const handleCustomSendModalClose = () => {
    setCustomModalVisible(false);
  };

  const handleCustomSendModalOpen = (type) => {
    setCustomContactType(type);
    setCustomModalVisible(true);
  };

  const actionOptions = [
    {
      label: 'Send Text',
      children: [
        patient?.phone && {
          label: `${patient?.phone} (primary)`,
          onClick: () => onSendText({ ids: [id], navigate, type: 'primary' }),
          icon: 'message-mono'
        },
        patient?.alt_phone && {
          label: `${patient?.alt_phone} (alternative)`,
          onClick: () => onSendText({ ids: [id], navigate, type: 'alternative' }),
          icon: 'message-mono'
        },
        { label: 'Custom', onClick: () => handleCustomSendModalOpen('text') }
      ].filter(Boolean)
    },
    {
      label: 'Send Email',
      children: [
        patient?.email && {
          label: `${patient?.email} (primary)`,
          onClick: () => onSendEmail({ ids: [id], navigate, type: 'primary' }),
          icon: 'mail-mono'
        },
        patient?.alt_email && {
          label: `${patient?.alt_email} (alternative)`,
          onClick: () => onSendEmail({ ids: [id], navigate, type: 'alternative' }),
          icon: 'mail-mono'
        },
        { label: 'Custom', onClick: () => handleCustomSendModalOpen('email') }
      ].filter(Boolean)
    },
    {
      label: 'Delete',
      onClick: onOpenDeleteDialog,
      icon: 'trash-mono',
      permission: { requiredPermissions: 'invoicing.delete' }
    }
  ];

  return (
    <>
      <Popover
        className="flex h-full w-full items-center justify-center"
        position="left"
        isFixed
        icon="context-menu-horizontal"
        options={actionOptions}
        isBorder={false}
      />
      {showConfirmationModal && (
        <Confirm
          variant="danger"
          primaryBtnTxt="Delete"
          title="Delete External Invoice"
          icon="trash-mono"
          message="Are you sure you want to delete this invoice?"
          loading={isDeleteInvoicesLoading}
          handleContinue={onDeleteInvoice}
          handleOpen={showConfirmationModal}
          handleClose={() => setShowConfirmationModal(false)}
        />
      )}
      {customModalVisible ? (
        <CustomSendModal
          ids={[id]}
          contactType={customContactType}
          visible={customModalVisible}
          onClose={handleCustomSendModalClose}
          onPrev={handleCustomSendModalClose}
        />
      ) : null}
    </>
  );
};

export default SendInvoiceMessagePopUp;
